<script lang="ts">
  import { onMount } from 'svelte';
  import { push } from 'svelte-spa-router';
  import Button from '../../Button.svelte';
  import Breadcrumbs from '../../Breadcrumbs.svelte';
  import BackButton from '../../BackButton.svelte';
  import LoadingSkeleton from '../../LoadingSkeleton.svelte';
  import ErrorState from '../../ErrorState.svelte';
  import Dropdown from '../../Dropdown.svelte';
  import Table from '../../Table.svelte';
  import StatusBadge from '../../StatusBadge.svelte';
  import UsersService, { type User, type UserProfile, type DonationRequest } from '../../../lib/api/admin/users.service';
  import userStatusesResourceService, { type UserStatus } from '../../../lib/api/resource/user-statuses.service';
  import toast from 'svelte-french-toast';

  export let userUuid: string = '';

  let isLoading = true;
  let error = '';
  let userProfile: UserProfile | null = null;
  let userStatuses: UserStatus[] = [];
  let statusOptions: Array<{ uuid: string; name: string }> = [];
  let selectedStatus = '';
  
  // Extract employers from donation requests
  interface EmployerInfo {
    uuid: string;
    name: string;
  }
  let linkedEmployers: EmployerInfo[] = [];
  let donationRequests: DonationRequest[] = [];

  // Track previous UUID to reload when it changes
  let previousUuid = '';
  $: if (userUuid && userUuid !== previousUuid) {
    previousUuid = userUuid;
    loadUserProfile();
  }

  // Breadcrumbs items
  $: breadcrumbsItems = [
    { label: 'Admin', href: '/admin/dashboard' },
    { label: 'Users', href: '/admin/users' },
    { label: userProfile?.full_name || 'User Profile' }
  ];

  async function loadUserProfile() {
    if (!userUuid) {
      error = 'User UUID is required';
      isLoading = false;
      return;
    }

    try {
      isLoading = true;
      error = '';

      const response = await UsersService.getUser(userUuid);
      
      if (response.success && response.data) {
        const profile = response.data;
        userProfile = profile;
        selectedStatus = profile.user_status?.uuid || '';
        
        // Extract donation requests and employers
        donationRequests = profile.donation_requests || [];
        extractEmployers();
        
        // Ensure status options are loaded if not already
        if (statusOptions.length === 0) {
          await loadUserStatuses();
        }
      } else {
        error = response.message || 'Failed to load user profile';
        toast.error(error);
      }
    } catch (err: any) {
      error = err.message || 'An error occurred while loading user profile';
      toast.error(error);
    } finally {
      isLoading = false;
    }
  }

  async function loadUserStatuses() {
    try {
      const response = await userStatusesResourceService.getUserStatuses();
      // Handle API response structure: response.data contains UserStatusesResponse
      // UserStatusesResponse = { success: boolean, data: { user_statuses: [...] } }
      if (response.success && response.data?.user_statuses) {
        userStatuses = response.data.user_statuses;
        // Filter to only show Active and Blocked statuses
        statusOptions = userStatuses
          .filter(status => {
            const name = status.name.toLowerCase();
            return name === 'active' || name === 'blocked';
          })
          .map(status => ({ uuid: status.uuid, name: status.name }));
      } else {
        console.warn('Unexpected response structure:', response);
        toast.error('Failed to load user statuses - unexpected response format');
      }
    } catch (err) {
      console.error('Error loading user statuses:', err);
      toast.error('Failed to load user statuses');
    }
  }

  async function handleStatusUpdate() {
    if (!userUuid || !selectedStatus) return;

    try {
      const response = await UsersService.updateUserStatus(userUuid, selectedStatus);
      if (response.success) {
        toast.success('User status updated successfully');
        await loadUserProfile();
      } else {
        toast.error(response.message || 'Failed to update user status');
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to update user status');
    }
  }

  let showDeleteModal = false;
  let deleting = false;

  async function handleDelete() {
    if (!userUuid) return;
    deleting = true;
    try {
      const response = await UsersService.deleteUser(userUuid);
      if (response.success) {
        toast.success('User deleted successfully');
        push('/admin/users');
      } else {
        toast.error(response.message || 'Failed to delete user');
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete user');
    } finally {
      deleting = false;
      showDeleteModal = false;
    }
  }

  function handleBack() {
    push('/admin/users');
  }

  function extractEmployers() {
    if (!donationRequests || donationRequests.length === 0) {
      linkedEmployers = [];
      return;
    }

    const employerMap = new Map<string, EmployerInfo>();
    
    donationRequests.forEach((donation) => {
      if (donation.employer && donation.employer.uuid) {
        if (!employerMap.has(donation.employer.uuid)) {
          employerMap.set(donation.employer.uuid, {
            uuid: donation.employer.uuid,
            name: donation.employer.name
          });
        }
      }
    });

    linkedEmployers = Array.from(employerMap.values());
  }

  function handleDonationRowClick(row: DonationRequest) {
    push(`/view-donation/${row.uuid}`);
  }

  function handleEmployerClick(employerUuid: string) {
    push(`/employer-profile/${employerUuid}`);
  }

  function formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const month = date.toLocaleDateString('en-US', { month: 'short' });
      const day = date.getDate();
      const year = date.getFullYear();
      return `${month} ${day}, ${year}`;
    } catch {
      return 'Invalid Date';
    }
  }

  function formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0);
  }

  // Table columns for donation requests
  const donationColumns = [
    {
      key: 'created_at',
      label: 'Date',
      width: '15%',
      render: (value: string) => formatDate(value)
    },
    {
      key: 'charity.name',
      label: 'Charity',
      width: '25%',
      render: (value: any, row: DonationRequest) => row.charity?.name || 'N/A'
    },
    {
      key: 'employer.name',
      label: 'Employer',
      width: '25%',
      render: (value: any, row: DonationRequest) => row.employer?.name || 'N/A'
    },
    {
      key: 'leave_hours',
      label: 'Hours',
      width: '12%',
      render: (value: number) => `${value || 0}h`
    },
    {
      key: 'leave_value',
      label: 'Value',
      width: '15%',
      render: (value: number, row: DonationRequest) => {
        const total = (value || 0) + (row.employer_top_up || 0);
        return formatCurrency(total);
      }
    },
    {
      key: 'donation_request_status.name',
      label: 'Status',
      width: '15%',
      render: (value: any, row: DonationRequest) => {
        const statusName = row.donation_request_status?.name || 'Unknown';
        return `<span class="status-badge status-${statusName.toLowerCase().replace(/\s+/g, '-')}">${statusName}</span>`;
      }
    }
  ];

  onMount(() => {
    loadUserStatuses();
    if (userUuid) {
      previousUuid = userUuid;
      loadUserProfile();
    } else {
      error = 'User UUID is required';
      isLoading = false;
    }
  });
</script>

<div class="user-profile">
  <Breadcrumbs items={breadcrumbsItems} />
  <BackButton text="Back to User Management" onClick={handleBack} />
  
  {#if isLoading}
    <div style="min-height: 400px; padding: 2rem;">
      <LoadingSkeleton lines={8} height="2rem" />
    </div>
  {:else if error}
    <ErrorState 
      message="Failed to load user profile" 
      description={error}
      onRetry={loadUserProfile}
    />
  {:else if userProfile}
    <!-- User Header Section -->
    <div class="profile-header">
      <div class="header-info">
        <h1 class="user-name">{userProfile.full_name}</h1>
        <div class="user-meta">
          <span class="meta-item">{userProfile.email}</span>
          {#if userProfile.phone}
            <span class="meta-separator">•</span>
            <span class="meta-item">{userProfile.phone}</span>
          {/if}
        </div>
      </div>
    </div>

    <!-- Profile Details Card -->
    <div class="profile-details-card">
      <div class="card-header">
        <div class="header-title-section">
          <h2>Profile Details</h2>
          {#if userProfile.user_status}
            <StatusBadge status={userProfile.user_status.name} />
          {/if}
        </div>
      </div>
      
      <div class="card-content">
        <div class="details-grid">
          <div class="detail-item">
            <span class="detail-label">Email</span>
            <span class="detail-value">{userProfile.email}</span>
          </div>
          {#if userProfile.phone}
            <div class="detail-item">
              <span class="detail-label">Phone</span>
              <span class="detail-value">{userProfile.phone}</span>
            </div>
          {/if}
          <div class="detail-item">
            <span class="detail-label">Status</span>
            <span class="detail-value">
              {userProfile.user_status?.name?.toLowerCase() === 'active' ? 'Active' : 'inactive'}
            </span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Role</span>
            <span class="detail-value">{userProfile.user_role?.name || 'N/A'}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Email Verified</span>
            <span class="detail-value">
              {userProfile.email_verified_at ? 'Yes' : 'No'}
            </span>
          </div>
          {#if userProfile.job_title}
            <div class="detail-item">
              <span class="detail-label">Job Title</span>
              <span class="detail-value">{userProfile.job_title}</span>
            </div>
          {/if}
          {#if userProfile.location}
            <div class="detail-item">
              <span class="detail-label">Location</span>
              <span class="detail-value">{userProfile.location}</span>
            </div>
          {/if}
          {#if userProfile.department}
            <div class="detail-item">
              <span class="detail-label">Department</span>
              <span class="detail-value">{userProfile.department}</span>
            </div>
          {/if}
          <div class="detail-item status-management-item">
            <span class="detail-label">Status Management</span>
            <div class="status-control-inline">
              <Dropdown
                options={statusOptions}
                selectedValue={selectedStatus}
                placeholder="Select Status"
                onSelectionChange={(value) => {
                  selectedStatus = value;
                }}
              />
              <Button variant="primary" on:click={handleStatusUpdate}>
                Update Status
              </Button>
            </div>
          </div>
          <div class="detail-item">
            <span class="detail-label">Actions</span>
            <div class="status-control-inline">
              <Button variant="danger" on:click={() => showDeleteModal = true}>
                Delete User
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>

    {#if showDeleteModal}
      <div class="modal-overlay" role="presentation" on:click|self={() => showDeleteModal = false} on:keydown={(e) => e.key === 'Escape' && (showDeleteModal = false)}>
        <div class="modal-content" role="dialog" aria-modal="true" aria-labelledby="delete-modal-title">
          <h3 id="delete-modal-title">Delete User</h3>
          <p>Are you sure you want to delete <strong>{userProfile?.full_name || 'this user'}</strong>? This action cannot be undone.</p>
          <div class="modal-actions">
            <Button variant="secondary" on:click={() => showDeleteModal = false} disabled={deleting}>Cancel</Button>
            <Button variant="danger" on:click={handleDelete} loading={deleting} disabled={deleting}>
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </div>
        </div>
      </div>
    {/if}

    <!-- Linked Employers Card -->
    {#if linkedEmployers.length > 0}
      <div class="employers-card">
        <div class="card-header">
          <h2>Linked Employers</h2>
        </div>
        <div class="card-content">
          <div class="employers-list">
            {#each linkedEmployers as employer}
              <div class="employer-item" on:click={() => handleEmployerClick(employer.uuid)} role="button" tabindex="0" on:keydown={(e) => e.key === 'Enter' && handleEmployerClick(employer.uuid)}>
                <span class="employer-name">{employer.name}</span>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
              </div>
            {/each}
          </div>
        </div>
      </div>
    {/if}

    <!-- Donation Requests Card -->
    <div class="donation-requests-card">
      <div class="card-header">
        <h2>Donation Requests</h2>
        {#if donationRequests.length > 0}
          <span class="badge">{donationRequests.length}</span>
        {/if}
      </div>
      <div class="card-content">
        {#if donationRequests.length === 0}
          <div class="empty-state">
            <p>No donation requests found for this user.</p>
          </div>
        {:else}
          <Table
            columns={donationColumns}
            data={donationRequests}
            onRowClick={handleDonationRowClick}
          />
        {/if}
      </div>
    </div>

  {/if}
</div>

<style>
  .user-profile {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    padding: 1.5rem 2rem;
    background-color: #f7fafc;
    min-height: 100vh;
  }

  .profile-header {
    background: white;
    border-radius: 12px;
    padding: 2rem;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  }

  .header-info {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .user-name {
    font-size: 2rem;
    font-weight: 700;
    color: #111827;
    margin: 0;
  }

  .user-meta {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #6b7280;
    font-size: 0.875rem;
  }

  .meta-separator {
    color: #d1d5db;
  }

  .profile-details-card,
  .employers-card,
  .donation-requests-card {
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  }

  .card-header {
    padding: 1.5rem 2rem;
    border-bottom: 1px solid #e5e7eb;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .header-title-section {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .card-header h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #111827;
    margin: 0;
  }

  .badge {
    display: inline-flex;
    align-items: center;
    padding: 0.25rem 0.75rem;
    background-color: #e5e7eb;
    color: #374151;
    border-radius: 9999px;
    font-size: 0.875rem;
    font-weight: 500;
  }

  .card-content {
    padding: 1.5rem 2rem;
  }

  .details-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1.5rem;
  }

  .detail-item {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .detail-label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #6b7280;
  }

  .detail-value {
    font-size: 1rem;
    color: #111827;
  }

  .status-management-item {
    grid-column: 1 / -1;
  }

  .status-control-inline {
    display: flex;
    align-items: center;
    gap: 1rem;
    flex-wrap: wrap;
  }

  .status-control-inline :global(button.primary) {
    background-color: var(--color-primary, #ffb400) !important;
    color: white !important;
    min-width: 200px;
    width: auto;
    padding: 0.5rem 1.5rem;
    border: none !important;
  }

  .status-control-inline :global(button.primary:hover:not(:disabled)) {
    background-color: var(--color-primary-hover, #f0a800) !important;
  }

  .status-control-inline :global(button.primary span:not(.invisible)) {
    color: white !important;
  }

  .status-control-inline :global(button.primary .loader) {
    display: none !important;
  }

  .employers-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .employer-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem;
    background-color: #f9fafb;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .employer-item:hover {
    background-color: #f3f4f6;
    border-color: var(--color-primary, #ffb400);
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  .employer-item:focus {
    outline: 2px solid var(--color-primary, #ffb400);
    outline-offset: 2px;
  }

  .employer-name {
    font-size: 1rem;
    font-weight: 500;
    color: #111827;
  }

  .empty-state {
    padding: 2rem;
    text-align: center;
    color: #6b7280;
  }

  :global(.status-badge) {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.25rem 0.75rem;
    border-radius: 9999px;
    font-size: 0.875rem;
    font-weight: 500;
  }

  :global(.status-pending) {
    background-color: #fef3c7;
    color: #92400e;
  }

  :global(.status-approved) {
    background-color: #dcfce7;
    color: #166534;
  }

  :global(.status-rejected) {
    background-color: #fee2e2;
    color: #991b1b;
  }

  :global(.status-processed) {
    background-color: #dbeafe;
    color: #1e40af;
  }

  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 2000;
  }

  .modal-content {
    background-color: #fff;
    border-radius: 10px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
    max-width: 450px;
    width: 90%;
    padding: 2rem;
  }

  .modal-content h3 {
    margin: 0 0 1rem 0;
    font-size: 1.25rem;
    color: #991b1b;
  }

  .modal-content p {
    color: #374151;
    margin: 0 0 1.5rem 0;
    line-height: 1.6;
  }

  .modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
  }
</style>

